import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardList, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { ownerInquiryReviewAPI } from '../../lib/ownerInquiryReviewApi';
import { extractApiErrorMessage } from '../../lib/ownerViewingSlotForm';
import {
  GUIDED_QUESTION_DEFINITIONS,
  OWNER_REVIEW_SAVE_BTN_TEST_ID,
  OWNER_REVIEW_SECTION_TEST_ID,
  SHORTLIST_STATUS_OPTIONS,
  buildOwnerReviewPatchPayload,
  defaultReviewFormState,
  ownerReviewWorkspaceTestId,
  parseOwnerReviewGetResponse,
  reviewFormStateFromApi,
} from '../../lib/ownerInquiryReviewDisplay';

export default function OwnerInquiryReviewWorkspace({ inquiry }) {
  const { t } = useLanguage();
  const inquiryId = inquiry?.id;

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [persisted, setPersisted] = useState(false);
  const [form, setForm] = useState(defaultReviewFormState);
  const [loadError, setLoadError] = useState('');

  const loadReview = useCallback(async () => {
    if (!inquiryId) {
      setForm(defaultReviewFormState());
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const res = await ownerInquiryReviewAPI.get(inquiryId);
      const parsed = parseOwnerReviewGetResponse(res.data);
      setPersisted(parsed.persisted);
      setForm(reviewFormStateFromApi(parsed.review));
    } catch (error) {
      console.error('Failed to load owner inquiry review:', error);
      const message = extractApiErrorMessage(
        error,
        t('owner.review_load_failed', 'Failed to load owner review'),
      );
      setLoadError(message);
      setForm(defaultReviewFormState());
    } finally {
      setLoading(false);
    }
  }, [inquiryId, t]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const handleGuidedAnswerChange = (questionId, value) => {
    setForm((prev) => ({
      ...prev,
      guidedAnswers: {
        ...prev.guidedAnswers,
        [questionId]: value,
      },
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!inquiryId) {
      return;
    }

    setSaving(true);
    try {
      const payload = buildOwnerReviewPatchPayload({
        shortlistStatus: form.shortlistStatus,
        privateNotes: form.privateNotes,
        guidedAnswers: form.guidedAnswers,
      });
      const res = await ownerInquiryReviewAPI.patch(inquiryId, payload);
      const parsed = parseOwnerReviewGetResponse(res.data);
      setPersisted(parsed.persisted);
      setForm(reviewFormStateFromApi(parsed.review));
      toast.success(t('owner.review_save_success', 'Review saved'));
    } catch (error) {
      console.error('Failed to save owner inquiry review:', error);
      toast.error(
        extractApiErrorMessage(error, t('owner.review_save_failed', 'Failed to save review')),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-4 rounded-lg border border-amber-200/80 bg-amber-50/40 p-3"
      data-testid={ownerReviewWorkspaceTestId(inquiryId)}
    >
      <button
        type="button"
        className="w-full flex items-start justify-between gap-2 text-left"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        data-testid={OWNER_REVIEW_SECTION_TEST_ID}
      >
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <ClipboardList className="w-4 h-4 text-amber-700 shrink-0" aria-hidden />
            {t('owner.review_section_title', 'Owner Review')}
          </h4>
          <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
            <Lock className="w-3 h-3 shrink-0" aria-hidden />
            {t(
              'owner.review_section_subtitle',
              'Private workspace to shortlist inquiries and record fit notes before you decide.',
            )}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" aria-hidden />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" aria-hidden />
        )}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-amber-200/60">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t('common.loading', 'Loading...')}
            </div>
          ) : (
            <>
              {loadError && (
                <p className="text-xs text-red-600 mb-2" data-testid="owner-review-load-error">
                  {loadError}
                </p>
              )}
              {!persisted && !loadError && (
                <p className="text-xs text-slate-500 mb-2">
                  {t('owner.review_unsaved_hint', 'No saved review yet. Your notes stay private until you save.')}
                </p>
              )}

              <form onSubmit={handleSave} className="space-y-3">
                <div>
                  <label
                    htmlFor={`review-shortlist-${inquiryId}`}
                    className="text-[10px] font-medium text-slate-700 block mb-1"
                  >
                    {t('owner.review_shortlist_label', 'Shortlist status')}
                  </label>
                  <select
                    id={`review-shortlist-${inquiryId}`}
                    value={form.shortlistStatus}
                    onChange={(e) => setForm((prev) => ({ ...prev, shortlistStatus: e.target.value }))}
                    className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 bg-white"
                    data-testid="owner-review-shortlist-select"
                  >
                    {SHORTLIST_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(option.labelKey, option.value)}
                      </option>
                    ))}
                  </select>
                  {form.shortlistStatus === 'selected_candidate' && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      {t(
                        'owner.review_selected_candidate_hint',
                        'This is a private marker only. It does not approve or decline the inquiry.',
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`review-notes-${inquiryId}`}
                    className="text-[10px] font-medium text-slate-700 block mb-1"
                  >
                    {t('owner.review_private_notes_label', 'Private owner notes')}
                  </label>
                  <p className="text-[10px] text-slate-500 mb-1">
                    {t(
                      'owner.review_private_notes_helper',
                      'Only you and platform admins can see these notes.',
                    )}
                  </p>
                  <textarea
                    id={`review-notes-${inquiryId}`}
                    value={form.privateNotes}
                    onChange={(e) => setForm((prev) => ({ ...prev, privateNotes: e.target.value }))}
                    rows={3}
                    className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 bg-white resize-y min-h-[4rem]"
                    data-testid="owner-review-private-notes"
                  />
                </div>

                <fieldset className="space-y-2">
                  <legend className="text-[10px] font-medium text-slate-700">
                    {t('owner.review_guided_questions_label', 'Guided review questions')}
                  </legend>
                  {GUIDED_QUESTION_DEFINITIONS.map((question) => (
                    <div key={question.id}>
                      <label
                        htmlFor={`review-q-${inquiryId}-${question.id}`}
                        className="text-[10px] text-slate-600 block mb-0.5"
                      >
                        {t(question.labelKey, question.id)}
                      </label>
                      <input
                        id={`review-q-${inquiryId}-${question.id}`}
                        type="text"
                        value={form.guidedAnswers[question.id] || ''}
                        onChange={(e) => handleGuidedAnswerChange(question.id, e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 bg-white"
                        data-testid={`owner-review-guided-${question.id}`}
                      />
                    </div>
                  ))}
                </fieldset>

                <Button
                  type="submit"
                  size="sm"
                  disabled={saving}
                  className="h-8 text-xs bg-amber-700 hover:bg-amber-800 text-white"
                  data-testid={OWNER_REVIEW_SAVE_BTN_TEST_ID}
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {t('owner.review_save_btn', 'Save review')}
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}
